import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button } from "flowbite-react";
import EmailInput from "../../../components/Input/EmailInput";
import { resetPassword } from "../../../services/authService";
import { useNavigate } from "react-router-dom";

interface RecoveryFormInputs {
    email: string;
}

const initialValues: RecoveryFormInputs = {
    email: "",
};

const schema = yup.object().shape({
    email: yup.string().email("Invalid email address").required("Email is required"),
});

const RecoveryForm: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<RecoveryFormInputs>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const onSubmit: SubmitHandler<RecoveryFormInputs> = async (data) => {
        setIsLoading(true);
        try {
            await resetPassword(data.email);
            navigate(`/account/recovery/success?email=${data.email}`, {
                replace: true,
            });
        } catch (error) {
            setIsLoading(false);
            console.error(error);
        }
    };

    return (
        <div className='flex-1 flex flex-col justify-center items-center p-4 lg:p-8 lg:bg-white'>
            <div className='mb-6 mx-auto flex flex-col text-center'>
                <img
                    alt={""}
                    loading='lazy'
                    src='/recovery-lock.png'
                    className='aspect-square object-contain object-center w-3/4 justify-center items-center overflow-hidden self-center max-w-full'
                />
                <h2 className='text-3xl font-bold text-white lg:text-black'>Lost your password?</h2>
                <h2 className='text-3xl font-bold text-white lg:text-black'>Enter your details to recover</h2>
                <p className='text-sm text-gray-300 lg:text-gray-500 mt-6'>Enter your details to proceed further</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className='w-full max-w-md'>
                <EmailInput
                    id='email'
                    label='Email'
                    placeholder='catherine.shaw@gmail.com'
                    register={register("email")}
                    errors={errors}
                />

                {isLoading ? (
                    <LoadingButton />
                ) : (
                    <Button
                        type='submit'
                        color='light'
                        fullSized
                        className='w-full flex items-center justify-center bg-purple-500 text-white py-3'>
                        Recover
                    </Button>
                )}
            </form>
        </div>
    );
};

const LoadingButton = () => {
    return (
        <Button
            disabled={true}
            type='submit'
            color='light'
            fullSized
            className='w-full flex items-center justify-center bg-purple-500 text-white py-3'>
            <svg
                aria-hidden='true'
                role='status'
                className='inline mr-3 w-4 h-4 text-white animate-spin'
                viewBox='0 0 100 101'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                    fill='#E5E7EB'></path>
                <path
                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                    fill='currentColor'></path>
            </svg>
            Loading...
        </Button>
    );
};

export default RecoveryForm;